import React from 'react';
import { useSelector } from 'react-redux';
import {
  PERSONA_TYPES,
  personaAliases,
  Persona,
  SharedRootStore,
} from '@cowbell/data';

type PersonaAlias = keyof typeof personaAliases;

export type UsePersonaReturn = ReturnType<typeof usePersona>;

const selectPersona = (state: SharedRootStore) =>
  state.ux.persona as Persona | undefined;
const selectLegacyPersona = (state: SharedRootStore) =>
  state.ux._legacy_persona as PersonaAlias | undefined;

export const usePersona = ({
  skip = false,
}: { readonly skip?: boolean } = {}) => {
  const persona = useSelector(selectPersona);
  const urlPersona = useSelector(selectLegacyPersona);

  return React.useMemo(
    () =>
      skip
        ? {}
        : {
            type: persona,
            personaHome: persona ? getHomePathByPersona(persona) : null,
            basePath: persona ? getBasePathByPersona(persona) : null,
            isCowbell: persona === PERSONA_TYPES.COWBELL,
            isAgency: persona === PERSONA_TYPES.AGENCY,
            isInsured: persona === PERSONA_TYPES.CUSTOMER,
            isDigital: persona === PERSONA_TYPES.DIGITAL,
            isMssp: persona === PERSONA_TYPES.MSSP,
            isCarrier: persona === PERSONA_TYPES.CARRIER,
            isCluster: persona === PERSONA_TYPES.CLUSTER,
            isVendor: persona === PERSONA_TYPES.VENDOR,
            isReinsurer: persona === PERSONA_TYPES.RE_INSURER,
            isPremiumProcessor: persona === PERSONA_TYPES.PREMIUM_PROCESSOR,
            isPool: persona === PERSONA_TYPES.POOL,
            _legacy_persona: urlPersona
              ? personaAliases[urlPersona]
              : undefined,
            /** @deprecated use type */
            persona,
          },
    [persona, urlPersona, skip]
  );
};

function getBasePathByPersona(persona: Persona): string {
  const map: Record<Persona, string> = {
    COWBELL: '/admin',
    AGENCY: '/agency',
    CUSTOMER: '/customer',
    DIGITAL: '/da',
    MSSP: '/mssp',
    CARRIER: '/carrier',
    CLUSTER: '/cluster',
    VENDOR: '/vendor',
    RE_INSURER: '/reinsurer',
    PREMIUM_PROCESSOR: '/premiumprocessor',
    POOL: '/pool',
    FRANCHISE: '/admin',
    UNKNOWN: '/unknown',
  };
  return map[persona];
}

function getHomePathByPersona(persona: Persona): string {
  // A franchise has no dashboard; /admin opens its first allowed page.
  if (persona === PERSONA_TYPES.FRANCHISE) {
    return getBasePathByPersona(persona);
  }
  return `${getBasePathByPersona(persona)}/dashboard`;
}
